/**
 * THE COVERED PICTURE — one picture uncovered a tile at a time.
 *
 * The teaser is the ground and the unpeeled tiles are opaque covers laid over it, so a peel is
 * watched *on the picture* rather than in a grid beside it: the cover fades and lifts in place and
 * the real tile is what is underneath it afterwards. A grid of squares next to a thumbnail made
 * the recipient hold two pictures in their head and join them up; this is the one picture that is
 * being uncovered, which is the thing actually happening.
 *
 * Privacy is unchanged and is not this file's doing: an unpeeled tile has no `image` in the
 * payload and no route to its bytes, so a cover is drawn over the teaser — never over pixels this
 * client was not given. The teaser shows through for the moment between a cover lifting and its
 * tile arriving, which is the only time it is ever seen.
 *
 * Geometry comes from the wrap: `prepareWrap` cuts a cols × cols grid out of the image without
 * squaring it, so every tile carries the whole picture's aspect and the frame set to that aspect
 * tiles exactly. A wrap that never recorded its size falls back to a square rather than guessing.
 *
 * WHAT A COVER DOES WHEN IT CANNOT BE LIFTED IS THE OTHER HALF OF THE CONTROL. A locked cover used
 * to be a `disabled` Pressable, which is a press that does nothing and says nothing — and the two
 * explanations the screen had for why were both behind that dead press. Every cover is pressable
 * now and the frame answers: `onPeel` when there is a credit to spend, `onLocked` when there is
 * not, `onRetry` for the one cell that is peeled and has no bytes. A frame with none of the three
 * is the giver's preview, and draws itself as paper with nothing to press.
 */
import { Text } from '@/components/ui/text';
import { useReducedMotion } from '@/lib/motion';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { Animated, Image, Platform, Pressable, View, type ViewStyle } from 'react-native';

/**
 * Every layer of a cell fills it, in inline style because its classes cannot reach it.
 *
 * ABSOLUTE rather than 100%-of-flow, and that is what makes the lift real. The cover and the tile
 * underneath it are stacked in the same box, so the moment the peel lands the tile can be mounted
 * while the cover is still fading over the top of it. Laid out in flow, the two would be two rows
 * and the arriving tile would shove the cover off the picture instead of appearing beneath it.
 */
const FILL = { position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 } as const;

/** How long a cover takes to fade and scale out of the way, once the peel has landed. */
const LIFT_MS = 240;

/**
 * The smallest a cover may be drawn, which is the smallest a finger can hit.
 *
 * The whole cell is the target — there is nothing smaller inside it — so this is a floor on the
 * FRAME rather than on the cover: a 16-tile wrap needs 4 × 44px of width before it can be laid
 * out honestly, and a frame that would be narrower is made wider instead of quietly shipping
 * 30px targets.
 */
const COVER_MIN = 44;

export interface CoverTile {
  idx: number;
  peeled: boolean;
  image?: string | null;
}

/**
 * THE WRAPPING — a cover's ground, read off the round's own gradient pair.
 *
 * `--gradient-from` / `--gradient-to` are the panel the header band is cut from: the booth's red
 * top and foot in Bouquet, the one flat midnight in Tuxedo. A cover is wrapping paper, so it is
 * drawn in the same stock the band is, which is what makes an unpeeled tile read as something
 * covering the picture rather than as another card sitting on it.
 *
 * Web gets the two stops; native gets `bg-gradient-to` as a flat sheet, which is the same bargain
 * `.fit-band` makes with `bg-band-foot`. Each cover carries its own gradient rather than a sheet
 * being drawn across the frame — a single layer behind the covers would be what a lifted cover
 * revealed, and what has to be under there is the teaser.
 */
const WRAPPING: ViewStyle | undefined = Platform.select({
  web: {
    backgroundImage: 'linear-gradient(180deg, hsl(var(--gradient-from)) 0%, hsl(var(--gradient-to)) 100%)',
  } as unknown as ViewStyle,
  default: undefined,
});

export function CoveredPicture({
  teaser,
  width,
  height,
  cols,
  grid,
  tiles,
  credits,
  canPeel,
  busy,
  lockedLabel,
  notice,
  onPeel,
  onLocked,
  onRetry,
  onLifted,
}: {
  teaser?: string | null;
  width?: number | null;
  height?: number | null;
  cols: number;
  grid: number;
  tiles: CoverTile[];
  /**
   * Unspent acts at the giver's price. Shown as the coin count in the corner, zero included —
   * omitted entirely for a frame that is nobody's to peel, where a coin would be a fiction.
   */
  credits?: number;
  /**
   * False when there is nothing to spend or the giver is holding it — a standing fact about the
   * wrap, which is what the dimmed covers report. A request being in the air is `busy` instead:
   * it only stops a second press, because dimming the whole frame for a moment every time one
   * cover lifts reads as the picture flinching.
   */
  canPeel?: boolean;
  busy?: boolean;
  /** Why a cover cannot be lifted, for the screen reader — the visible reason is `notice`. */
  lockedLabel?: string;
  /**
   * The answer to a locked press, drawn ON THE FRAME along its foot.
   *
   * It belongs here rather than under the picture because it is what the press did: a sentence
   * that appeared below the fold, under a cover that visibly did nothing, was a sentence nobody
   * connected to their own tap. The words and the way out are the caller's — this file knows a
   * cover was pressed and nothing about what it costs.
   */
  notice?: React.ReactNode;
  /**
   * Peels one tile. Answers TRUE if it happened: a cover only lifts on a yes, because a cover
   * that faded out and came back is the picture being taken away again.
   */
  onPeel?: (idx: number) => Promise<boolean> | boolean;
  /** A cover was pressed with nothing to spend. The caller puts its reason in `notice`. */
  onLocked?: () => void;
  /** A peeled tile whose bytes did not arrive was pressed. */
  onRetry?: (idx: number) => void;
  /**
   * A cover has finished lifting and the tile under it is on show.
   *
   * This is where the caller reconciles with the server, and it is deliberately AFTER the
   * animation: a refetch that lands mid-lift can turn the last cover's frame into the whole
   * photograph, which cuts the one moment the whole panel exists for down to four frames.
   */
  onLifted?: (idx: number) => void;
}) {
  const [lifting, setLifting] = React.useState<number | null>(null);
  const cover = React.useRef(new Animated.Value(1)).current;
  const scale = React.useRef(cover.interpolate({ inputRange: [0, 1], outputRange: [1.12, 1] })).current;
  const reduceMotion = useReducedMotion();

  /**
   * Nothing wrapped yet.
   *
   * No teaser AND no covers: the second half matters, because a wrap whose teaser has not loaded
   * still has sixteen real covers over it and saying "nothing" over the top of them would be a
   * lie about a present that exists. The frame keeps its shape so the panel does not jump when
   * one arrives.
   */
  if (!teaser && tiles.length === 0) {
    return (
      <View className="bg-card border-border w-full items-center justify-center overflow-hidden rounded-lg border p-6" style={{ aspectRatio: 1 }}>
        <Text variant="small" className="text-muted-foreground text-center">
          Nothing wrapped for you
        </Text>
      </View>
    );
  }

  const rows = Math.max(1, Math.round(grid / cols));
  const cellWidth = share(cols);
  const cellHeight = share(rows);
  const aspect = width && height ? width / height : 1;
  /** Whether these covers are anybody's to lift. False on the giver's preview of their own wrap. */
  const live = !!onPeel;

  /**
   * The POST first, then the lift, and the tile is already mounted under the cover by the time it
   * starts moving.
   *
   * `onPeel` applies the server's answer for this one tile, so the render that follows it has the
   * real bytes in the cell — under an absolutely-positioned cover that has not moved yet. Then the
   * cover fades and what is revealed is the photograph rather than the teaser. Refused peels never
   * animate at all: `onPeel` answering false leaves the cover exactly where it was.
   */
  const lift = async (idx: number) => {
    let lifted = false;
    setLifting(idx);
    try {
      lifted = !!(await onPeel?.(idx));
      if (!lifted) return;
      if (reduceMotion) {
        cover.setValue(0);
      } else {
        await new Promise<void>((done) => {
          Animated.timing(cover, {
            toValue: 0,
            duration: LIFT_MS,
            useNativeDriver: Platform.OS !== 'web',
          }).start(() => done());
        });
      }
    } finally {
      // Either the tile arrived and this cover is about to unmount, or the peel did not happen and
      // the cover has to be back at full strength — a cover left faded over a tile nobody earned
      // would read as the picture having been given away.
      cover.setValue(1);
      setLifting(null);
      if (lifted) onLifted?.(idx);
    }
  };

  return (
    <View
      className="bg-muted border-border w-full overflow-hidden rounded-lg border"
      style={{ aspectRatio: aspect, minWidth: cols * COVER_MIN }}>
      {teaser ? (
        <Image
          source={{ uri: teaser }}
          className="absolute inset-0 h-full w-full"
          resizeMode="cover"
          // The teaser is twelve pixels wide. Smoothing it back up would invent detail the giver
          // did not send, so on the web it stays honest and blocky. Native has no equivalent —
          // `resizeMode` decides the filtering and neither platform exposes nearest-neighbour —
          // so a phone renders it smoothed. Accepted: it is a blur either way.
          style={Platform.OS === 'web' ? ({ imageRendering: 'pixelated' } as never) : undefined}
        />
      ) : null}

      <View className="absolute inset-0 flex-row flex-wrap">
        {tiles.map((tile) => {
          /**
           * PEELED, AND NO BYTES. It is earned, it is paid for, and the cell used to render as
           * nothing at all — a hole in the picture with the frame's grey showing through, which
           * reads as a tile the giver never sent. It is a fetch that did not land, so it says so
           * and the press asks again.
           */
          const missing = tile.peeled && !tile.image;
          const press = tile.peeled
            ? missing && onRetry
              ? () => onRetry(tile.idx)
              : undefined
            : canPeel && onPeel
              ? () => void lift(tile.idx)
              : onLocked;

          const cell = (
            <>
              {tile.peeled ? (
                tile.image ? (
                  <Image source={{ uri: tile.image }} style={FILL} resizeMode="cover" />
                ) : (
                  <View style={FILL} className="bg-card border-border items-center justify-center gap-0.5 border p-1">
                    <Text variant="small" className="text-muted-foreground">
                      ↻
                    </Text>
                    {/* One cell of a 4 × 4 grid is under 80px on a phone, so the CELL carries the
                        glyph and one word and the sentence goes under the frame, where there is a
                        line to say it on. `numberOfLines` is the floor: a clipped word is still a
                        word, and a second line here would push the glyph out of its own tile. */}
                    <Text variant="label" numberOfLines={1}>
                      Retry
                    </Text>
                  </View>
                )
              ) : null}
              {/* The cover outlives the tile's arrival by exactly one animation — see `lift`. */}
              {!tile.peeled || lifting === tile.idx ? (
                /**
                 * The animation and the drawing are two elements, and they have to be.
                 *
                 * `Animated.View` is not one of the components NativeWind registers, so a
                 * `className` on it is resolved and then dropped — which is how every cover on the
                 * web ended up 19px tall (the height of its own numeral) with the picture showing
                 * through around it, since `h-full w-full` never landed. So the Animated.View
                 * carries ONLY animated style, as an inline fill, and the plain View inside it is
                 * what wears the classes.
                 */
                <Animated.View style={[FILL, lifting === tile.idx ? { opacity: cover, transform: [{ scale }] } : null]}>
                  <View
                    style={WRAPPING}
                    className={cn(
                      // The wrapping's own stock rather than the frame's ground, so the moment a
                      // cover lifts there is something underneath it to have been covering. The
                      // hairline is the cut between two tiles of paper, and it is what makes each
                      // one legible as its own press target.
                      'bg-gradient-to border-border h-full w-full items-center justify-center border',
                      live && !canPeel && 'opacity-70',
                    )}>
                    <Text variant="small" className="text-band-ink">
                      {tile.idx + 1}
                    </Text>
                  </View>
                </Animated.View>
              ) : null}
            </>
          );

          const size = { width: `${cellWidth}%`, height: `${cellHeight}%` } as const;
          // A cell with nothing to say to a press is a View. A disabled Pressable is still a
          // button to a screen reader, and a peeled tile announcing itself as one is a promise.
          return press ? (
            <Pressable
              key={tile.idx}
              accessibilityRole="button"
              accessibilityLabel={
                missing
                  ? `Tile ${tile.idx + 1} did not load. Try again`
                  : canPeel
                    ? `Peel tile ${tile.idx + 1}`
                    : `Tile ${tile.idx + 1}, ${lockedLabel ?? 'not earned yet'}. What it costs`
              }
              disabled={!!busy || lifting !== null}
              onPress={press}
              style={size}>
              {cell}
            </Pressable>
          ) : (
            <View key={tile.idx} style={size}>
              {cell}
            </View>
          );
        })}
      </View>

      {/**
       * The coin, in the frame rather than under it: what a cover costs and what is left to spend
       * are the two halves of one decision, and the decision is made looking at the picture. It
       * takes no touches — it sits over the top-right cover, and a counter that swallowed that
       * one tile's press would read as one cover being stuck.
       */}
      {credits === undefined ? null : (
        <View
          pointerEvents="none"
          className="bg-card border-border absolute right-2 top-2 items-center rounded-md border px-3 py-1.5">
          <View className="flex-row items-center gap-1.5">
            <Text variant="small" className="text-primary">
              ●
            </Text>
            <Text variant="small" className="font-display tabular-nums font-bold">
              {credits}
            </Text>
          </View>
          <Text variant="label">to spend</Text>
        </View>
      )}

      {notice ? (
        <View className="bg-card border-border absolute inset-x-0 bottom-0 gap-2 border-t p-3">{notice}</View>
      ) : null}
    </View>
  );
}

/**
 * One cell's share of the frame, as a percentage that is always a shade *under* its true share.
 *
 * Three thirds of 33.3333% come to less than a whole row, where three of `100 / 3` come to a
 * hair over it — and a hair over is what wraps a 3 × 3 grid's last cover onto a fourth line.
 * Flooring rather than rounding is the whole of the fix; the sub-pixel remainder at the right
 * edge sits under the frame's own border.
 */
function share(n: number) {
  return Math.floor((100 / n) * 10_000) / 10_000;
}
